import { DOCUMENT, Injectable, inject } from '@angular/core';

/** Подстрока, по которой узнаём dark-блок в сгенерированном PrimeNG CSS.
 *  Ловит оба селектора из `darkModeSelector` — и `.p-dark{`, и
 *  `:root.p-dark{` (см. @theme/primeng-theme.config). */
const DARK_RULE = '.p-dark{';

/** PrimeNG держит сгенерированную тему в <style data-primeng-style-id="…">
 *  внутри <head> — по одному элементу на `semantic-variables`,
 *  `primitive-variables` и на каждый компонент. */
const STYLE_SELECTOR = 'style[data-primeng-style-id]';

/** Потолок восстановлений. Чужой бандл пишет свою тему один раз при
 *  бутстрапе, так что реально хватает единиц. Счётчик тут не ради
 *  экономии, а чтобы патологический случай (два рантайма, которые пишут
 *  наперегонки) не превратился в бесконечный пинг-понг. */
const MAX_RESTORES = 50;

/**
 * Возвращает тему платформы на место, если её затёр чужой PrimeNG.
 *
 * Зачем это вообще нужно. На `/agents/:id` мы сами подключаем loader
 * embeddable-виджета — он поднимает в этой же вкладке ВТОРОЙ Angular-бандл
 * со своим PrimeNG. А PrimeNG ищет свои style-элементы так:
 *
 *     document.querySelector(`style[data-primeng-style-id="${name}"]`)
 *
 * — то есть document-wide. Второй рантайм находит НАШИ элементы и
 * перезаписывает им textContent своей темой. Это не борьба каскадов:
 * наши правила физически удаляются из документа.
 *
 * Пока обе сборки собраны из одного коммита, перезапись безвредна — CSS
 * побайтово тот же. Но бандлы деплоятся ПОРОЗНЬ, и на проде виджет
 * отставал от админки на три месяца: он собран с `darkModeSelector: false`,
 * из-за чего в его CSS нет ни одного dark-правила. Результат (SKN-425,
 * замерено на app.speaknode.com): `semantic-variables` худеет с 10781 до
 * 6616 символов, блок `.p-dark{` исчезает, и вся тёмная админка
 * отрисовывается светлыми токенами PrimeNG — белые инпуты, селекты и
 * карточки поверх тёмного фона.
 *
 * Общий theme-конфиг (@theme/primeng-theme.config) убирает расхождение в
 * ИСХОДНИКАХ, но не защищает от рассинхрона ДЕПЛОЕВ. Этот сторож —
 * защита именно от второго: платформа не должна зависеть от того,
 * насколько свежий бандл сейчас отдаёт widget-хост.
 *
 * Как работает: запоминает последнее содержимое каждого style-элемента,
 * в котором был dark-блок, и возвращает его, если блок пропал. Стили без
 * dark-блока (у части компонентов его в препсете просто нет) не трогает —
 * для них нечего запоминать, и правило «потерял dark» не срабатывает.
 */
@Injectable({ providedIn: 'root' })
export class PrimeNgThemeGuard {
  private readonly document = inject(DOCUMENT);

  /** styleId → последнее содержимое, в котором был dark-блок. */
  private readonly pristine = new Map<string, string>();

  private observer: MutationObserver | null = null;
  private scheduled = false;
  private restores = 0;

  start(): void {
    if (this.observer || typeof MutationObserver === 'undefined') return;

    // characterData + childList: PrimeNG сначала аппендит пустой <style>,
    // и только потом присваивает textContent — это две разные мутации.
    this.observer = new MutationObserver(() => this.schedule());
    this.observer.observe(this.document.head, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    this.sync();
  }

  stop(): void {
    this.observer?.disconnect();
    this.observer = null;
  }

  /** Коалесим: на бутстрапе PrimeNG выдаёт десятки мутаций подряд, а
   *  реагировать достаточно один раз на микротаск. */
  private schedule(): void {
    if (this.scheduled) return;
    this.scheduled = true;
    queueMicrotask(() => {
      this.scheduled = false;
      this.sync();
    });
  }

  private sync(): void {
    const styles = this.document.head.querySelectorAll<HTMLStyleElement>(STYLE_SELECTOR);

    styles.forEach((el) => {
      const id = el.getAttribute('data-primeng-style-id');
      if (!id) return;

      const css = el.textContent ?? '';

      // Dark-блок на месте — это и есть эталон.
      if (css.includes(DARK_RULE)) {
        this.pristine.set(id, css);
        return;
      }

      // Блока нет. Если раньше он был — элемент переписали, возвращаем.
      const saved = this.pristine.get(id);
      if (!saved || this.restores >= MAX_RESTORES) return;

      this.restores++;
      el.textContent = saved;
    });
  }
}
